import { logger } from "../utils/logger";
import { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../utils/apiConfig';

export interface SlugData {
  pages: Record<string, string>;
  services: Array<{ id: string; name: string; slug: string }>;
  industries: Array<{ id: string; name: string; slug: string }>;
  blogs: Array<{ id: string; title: string; slug: string }>;
  legal: Record<string, string>;
  _metadata: {
    totalSlugs: number;
    lastUpdated: string;
  };
}

export interface UseSlugsReturn {
  slugs: SlugData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// ── Module-level cache (shared across all hook instances) ──
let cachedSlugs: SlugData | null = null;
let cacheTimestamp: number = 0;
let isFetching: boolean = false; // ← prevents concurrent fetches
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// ── Listeners — only for explicit cache clears, NOT after every fetch ──
const listeners = new Set<() => void>();

function getDefaultSlugs(): SlugData {
  return {
    pages: {
      homepage: '/',
      about: '/about-us',
      process: '/our-process',
      contact: '/contact',
      roi: '/calculate-roi',
      videos: '/videos',
      blog: '/blog',
      bundles: '/bundles',
    },
    services: [],
    industries: [],
    blogs: [],
    legal: {
      privacy: '/privacy',
      terms: '/terms',
      cookies: '/cookie-policy',
    },
    _metadata: {
      totalSlugs: 11,
      lastUpdated: new Date().toISOString(),
    },
  };
}

async function fetchSlugsFromAPI(): Promise<SlugData> {
  try {
    const response = await fetch(getApiUrl('/api/slugs/all'));
    if (!response.ok) return getDefaultSlugs();
    const result = await response.json();
    if (!result.success) return getDefaultSlugs();
    return result.data;
  } catch {
    return getDefaultSlugs();
  }
}

// ── Single shared fetch — no concurrent calls, no listener notification on success ──
async function getSlugs(forceRefresh: boolean = false): Promise<SlugData> {
  const now = Date.now();

  // Return cache if still valid
  if (!forceRefresh && cachedSlugs && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedSlugs;
  }

  // If already fetching, wait and return whatever cache exists (or default)
  if (isFetching) {
    return cachedSlugs || getDefaultSlugs();
  }

  isFetching = true;
  try {
    cachedSlugs = await fetchSlugsFromAPI();
    cacheTimestamp = Date.now();
    return cachedSlugs;
  } finally {
    isFetching = false;
  }
  // ← No notifyListeners() here — that was the loop trigger
}

// ── Only call this from CMS editor after saving slugs ──
export function clearSlugCache() {
  cachedSlugs = null;
  cacheTimestamp = 0;

  // Notify listeners ONCE so components refetch
  listeners.forEach(listener => listener());

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('slugCacheCleared'));
  }
}

export function useSlugs(): UseSlugsReturn {
  const [slugs, setSlugs] = useState<SlugData | null>(cachedSlugs);
  const [loading, setLoading] = useState<boolean>(!cachedSlugs);
  const [error, setError] = useState<string | null>(null);

  const fetchSlugs = useCallback(async (forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSlugs(forceRefresh);
      setSlugs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch slugs');
    } finally {
      setLoading(false);
    }
  }, []); // ← empty deps, stable reference

  const refresh = useCallback(async () => {
    await fetchSlugs(true);
  }, [fetchSlugs]);

  // Initial fetch — runs once on mount
  useEffect(() => {
    if (cachedSlugs && (Date.now() - cacheTimestamp) < CACHE_DURATION) {
      setSlugs(cachedSlugs);
      setLoading(false);
      return; // cache is fresh, don't fetch
    }
    fetchSlugs(false);
  }, []); // ← empty deps, runs once only

  // Listen for explicit cache clears (e.g. after CMS save)
  useEffect(() => {
    const handleCacheCleared = () => {
      fetchSlugs(true);
    };

    listeners.add(handleCacheCleared);
    if (typeof window !== 'undefined') {
      window.addEventListener('slugCacheCleared', handleCacheCleared);
    }

    return () => {
      listeners.delete(handleCacheCleared);
      if (typeof window !== 'undefined') {
        window.removeEventListener('slugCacheCleared', handleCacheCleared);
      }
    };
  }, [fetchSlugs]);

  return { slugs, loading, error, refresh };
}

export function usePageSlug(pageId: keyof SlugData['pages']): string | null {
  const { slugs } = useSlugs();
  return slugs?.pages[pageId] || null;
}

export function useServiceSlug(serviceId: string): string | null {
  const { slugs } = useSlugs();
  return slugs?.services.find(s => s.id === serviceId)?.slug || null;
}

export function useIndustrySlug(industryId: string): string | null {
  const { slugs } = useSlugs();
  return slugs?.industries.find(i => i.id === industryId)?.slug || null;
}

export function usePageSlugs(): Record<string, string> {
  const { slugs } = useSlugs();
  return slugs?.pages || {};
}